import { useMemo } from 'react';
import { Icon } from '../chrome/Icon';
import { AvatarButton } from '../chrome/Avatar';
import { ActivityCard } from './ActivityCard';
import { ACTIVITY_CATEGORY_META } from './activityMeta';
import { activityFitsAge } from './activityAge';
import {
  useActivities,
  useActivitySuggestions,
  useAiSuggestions,
  useRequestSuggestions,
  useSetActivityState,
  useAllBabyMedia,
} from '../../data/queries';
import type { ActivitySummary, MediaRow } from '../../data/dto';
import { useT } from '../../i18n';
import type { Baby } from '../../data/types';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useCatalogOrBlob } from '../media/useMediaBlob';
import { hash, rng } from '../shared/rng';

interface Props {
  baby: Baby;
  onOpenDetail: (activityId: number) => void;
  onOpenProfile?: () => void;
  onOpenCategory?: (category: 'play' | 'sing' | 'music' | 'read' | 'excursion' | 'favorites') => void;
}

/**
 * Activity List screen.
 *
 * Top: top suggestions for today with "New ideas" (re-roll) and
 * "AI ideas" (DeepSeek) actions. Below: the full catalog split
 * into 4 horizontal scrollable rows (Play, Sing, Adventure, Read).
 */
export function Activities({ baby, onOpenDetail, onOpenProfile, onOpenCategory }: Props) {
  const t = useT();

  const listQ      = useActivities(baby.id ?? null);
  const suggestQ   = useActivitySuggestions(baby.id ?? null);
  const reroll     = useRequestSuggestions();
  const aiSuggest  = useAiSuggestions();

  const catalog = listQ.data ?? [];

  // The all-categories overview only shows activities appropriate for the baby's
  // current age. (The per-category list offers Younger/Older/All escape hatches.)
  const agedCatalog = useMemo(
    () => catalog.filter((a) => activityFitsAge(a, baby.ageWeeks)),
    [catalog, baby.ageWeeks],
  );

  const mediaQ = useAllBabyMedia(baby.id ?? null);
  const enc = useEncryption();

  const { media: heroMedia, isCatalog: heroIsCatalog } = useMemo<{ media: MediaRow | null; isCatalog: boolean }>(() => {
    // 1. Try to find the last user-uploaded image for the activity category
    if (mediaQ.data) {
      const lastActivityImg = mediaQ.data.find(
        (m) =>
          m.media_type === 'image' &&
          (m.entity_type === 'activity' || m.entity_type === 'baby_activity_completion')
      );
      if (lastActivityImg) {
        return { media: lastActivityImg, isCatalog: false };
      }
    }

    // 2. Fall back to a random catalog image from all activities
    const catalogImages: { id: string; media_type: string; mime_type: string; is_e2e_encrypted: boolean }[] = [];
    for (const act of catalog) {
      if (act.media) {
        for (const m of act.media) {
          if (m.media_type === 'image') {
            catalogImages.push({
              id: m.id,
              media_type: m.media_type,
              mime_type: m.mime_type,
              is_e2e_encrypted: m.is_e2e_encrypted,
            });
          }
        }
      }
    }

    if (catalogImages.length > 0) {
      const seed = baby.id ? hash(baby.id) : 12345;
      const index = Math.floor(rng(seed)() * catalogImages.length);
      const img = catalogImages[index];
      if (img) {
        return {
          media: {
            id: img.id,
            media_type: img.media_type,
            mime_type: img.mime_type,
            is_e2e_encrypted: img.is_e2e_encrypted,
          } as MediaRow,
          isCatalog: true,
        };
      }
    }

    return { media: null, isCatalog: false };
  }, [mediaQ.data, catalog, baby.id]);

  const { state: heroState, url: heroUrl } = useCatalogOrBlob(
    (heroMedia ?? {}) as MediaRow,
    heroIsCatalog,
    enc.status === 'ready',
    enc.decrypt,
    !!heroMedia,
  );

  // The suggestion strip shows AI/re-roll results when present, else the daily set.
  // Rule-based picks (daily + re-roll) are trimmed to the baby's age — matching
  // the Today strip — while AI ideas are shown as-is since the parent asked for them.
  const override = aiSuggest.data ?? reroll.data;
  const rawSuggestions: ActivitySummary[] = override?.suggestions ?? suggestQ.data?.suggestions ?? [];
  const suggestions: ActivitySummary[] = override?.source === 'ai'
    ? rawSuggestions
    : rawSuggestions.filter((a) => activityFitsAge(a, baby.ageWeeks));
  const aiBusy = aiSuggest.isPending;

  return (
    <div className="relative -mx-5 nav:-mx-8 -mt-4 nav:-mt-10 pb-6 bg-bg min-h-dvh">
      {/* Hero Header Area */}
      <div className="relative h-60 overflow-hidden bg-tint">
        {heroState === 'ready' && heroUrl ? (
          <img src={heroUrl} alt="" className="size-full object-cover" />
        ) : heroState === 'loading' ? (
          <div className="size-full flex items-center justify-center bg-tint">
            <span className="size-6 animate-pulse rounded-full bg-grey-soft" />
          </div>
        ) : heroState === 'locked' ? (
          <div className="size-full flex flex-col items-center justify-center bg-tint px-6 text-center">
            <button
              onClick={() => void enc.ensureReady()}
              className="flex flex-col items-center justify-center gap-2 text-accent-ink hover:opacity-80 transition-opacity cursor-pointer"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              <span className="text-[11px] font-bold uppercase tracking-wider">{t('common.locked')}</span>
            </button>
          </div>
        ) : (
          <div className="size-full bg-gradient-to-tr from-accent/25 via-grey/15 to-tint" />
        )}
        
        {/* 60% overlay gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent 60%, var(--color-bg) 100%)' }}
        />
      </div>

      {/* Body Content */}
      <div className="relative z-2 px-5 nav:px-8 -mt-10 flex flex-col gap-5">
        {/* Header */}
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-3">{t('activities.title', 'Activities')}</p>
            <h1 className="mt-1 font-serif text-[28px] nav:text-3xl leading-tight tracking-tight text-ink">
              {t('activities.subtitle', 'Things to do together')}
            </h1>
          </div>
          <div className="nav:hidden">
            {onOpenProfile && <AvatarButton size={40} baby={baby} onOpenProfile={onOpenProfile} />}
          </div>
        </header>

      {/* Suggestions */}
      <section>
        <div className="mb-2 flex items-baseline justify-between px-1">
          <span className="text-[11px] font-bold uppercase tracking-[0.13em] text-ink-3">
            {override?.source === 'ai' ? t('activities.aiPicks', 'AI picks for {name}').replace('{name}', baby.name) : t('activities.suggestedToday', 'Top Suggestions for Today')}
          </span>
        </div>

        {suggestions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line bg-surface p-5 text-[13px] italic text-ink-3">
            {aiBusy ? t('activities.suggestBusy', 'Thinking of ideas…') : t('activities.suggestNone', 'No suggestions right now — try “New ideas” below.')}
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {suggestions.slice(0, 3).map((a) => (
              <ActivityCard key={a.id} activity={a} onOpen={() => onOpenDetail(a.id)} babyId={baby.id} />
            ))}
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => baby.id && reroll.mutate({ babyId: baby.id })}
            disabled={reroll.isPending}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 disabled:opacity-60"
          >
            <Icon name="refresh" size={14} />
            {reroll.isPending ? t('activities.suggestRerollBusy', 'Finding…') : t('activities.suggestReroll', 'New ideas')}
          </button>
          <button
            onClick={() => baby.id && aiSuggest.mutate({ babyId: baby.id })}
            disabled={aiBusy}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-[12.5px] font-semibold text-bg disabled:opacity-60"
          >
            <span className="size-1.5 rounded-full bg-accent" />
            {aiBusy ? t('activities.suggestAiBusy', 'Generating…') : t('activities.suggestAi', 'AI ideas')}
          </button>
        </div>
        {aiSuggest.isError && (
          <p className="mt-2 text-[12px] leading-snug text-ink-2">
            {t('activities.suggestError', "Couldn't generate AI ideas right now — please try again in a moment.")}
          </p>
        )}
      </section>

      {/* Catalog */}
      <section className="flex flex-col gap-6 mt-2">
        {listQ.isLoading ? (
          <p className="px-1 py-4 text-[13px] italic text-ink-3">{t('activities.catalog.loading', 'Loading activities…')}</p>
        ) : catalog.length === 0 ? (
          <p className="px-1 py-4 text-[13px] italic text-ink-3">
            {t('activities.catalog.none', 'No activities in this category yet.')}
          </p>
        ) : agedCatalog.length === 0 ? (
          <p className="px-1 py-4 text-[13px] italic text-ink-3">
            {t('activities.catalog.noneForAge', 'No activities matching {name}’s age right now.').replace('{name}', baby.name)}
          </p>
        ) : (
          <>
            {agedCatalog.filter((a) => a.is_favorite).length > 0 && (
              <CategoryRow
                title={t('activities.category.favorites', 'Favorites')}
                category="favorites"
                items={agedCatalog.filter((a) => a.is_favorite)}
                onOpen={onOpenDetail}
                babyId={baby.id}
                onSeeAll={onOpenCategory ? () => onOpenCategory('favorites') : undefined}
              />
            )}
            <CategoryRow
              title={t('activities.category.play', 'Play')}
              category="play"
              items={agedCatalog.filter((a) => a.category === 'play')}
              onOpen={onOpenDetail}
              babyId={baby.id}
              onSeeAll={onOpenCategory ? () => onOpenCategory('play') : undefined}
            />
            <CategoryRow
              title={t('activities.category.sing', 'Sing')}
              category="sing"
              items={agedCatalog.filter((a) => a.category === 'sing')}
              onOpen={onOpenDetail}
              babyId={baby.id}
              onSeeAll={onOpenCategory ? () => onOpenCategory('sing') : undefined}
            />
            <CategoryRow
              title={t('activities.category.music', 'Music')}
              category="music"
              items={agedCatalog.filter((a) => a.category === 'music')}
              onOpen={onOpenDetail}
              babyId={baby.id}
              onSeeAll={onOpenCategory ? () => onOpenCategory('music') : undefined}
            />
            <CategoryRow
              title={t('activities.category.excursion', 'Adventure')}
              category="excursion"
              items={agedCatalog.filter((a) => a.category === 'excursion')}
              onOpen={onOpenDetail}
              babyId={baby.id}
              onSeeAll={onOpenCategory ? () => onOpenCategory('excursion') : undefined}
            />
            <CategoryRow
              title={t('activities.category.read', 'Read')}
              category="read"
              items={agedCatalog.filter((a) => a.category === 'read')}
              onOpen={onOpenDetail}
              babyId={baby.id}
              onSeeAll={onOpenCategory ? () => onOpenCategory('read') : undefined}
            />
          </>
        )}
      </section>
      </div>
    </div>
  );
}

function CategoryRow({
  title,
  category,
  items,
  onOpen,
  babyId,
  onSeeAll,
}: {
  title: string;
  category?: 'play' | 'sing' | 'music' | 'read' | 'excursion' | 'favorites';
  items: ActivitySummary[];
  onOpen: (id: number) => void;
  babyId?: string | null;
  onSeeAll?: () => void;
}) {
  const t = useT();
  if (items.length === 0) return null;

  const displayedItems = items.slice(0, 5);
  const showMoreCard = items.length > 5 && onSeeAll;

  const meta = category && category !== 'favorites'
    ? (ACTIVITY_CATEGORY_META[category] || { color: 'var(--color-accent)' })
    : { color: 'var(--color-accent)' };

  return (
    <div>
      <div className="mb-2.5 flex items-baseline justify-between px-1">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.13em] text-ink-3">
          {title}
        </h3>
        {onSeeAll && (
          <button
            onClick={onSeeAll}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-2 active:scale-95 transition-transform"
          >
            {t('common.seeAll', 'See All')}
            <Icon name="chevron" size={12} color="var(--color-ink-2)" />
          </button>
        )}
      </div>
      <div className="scrollbar-none flex gap-4 overflow-x-auto snap-x snap-mandatory pb-1 px-1">
        {displayedItems.map((activity) => (
          <ActivityAirbnbCard
            key={activity.id}
            activity={activity}
            onClick={() => onOpen(activity.id)}
            babyId={babyId}
          />
        ))}
        {showMoreCard && (
          <div
            role="button"
            tabIndex={0}
            onClick={onSeeAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSeeAll();
              }
            }}
            className="w-[45%] shrink-0 snap-start flex flex-col text-left group cursor-pointer outline-none"
          >
            <div
              className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-dashed border-line flex flex-col items-center justify-center gap-1.5 transition-all group-active:scale-95 duration-200"
              style={{
                borderColor: `color-mix(in oklch, ${meta.color} 30%, var(--color-line))`,
                backgroundColor: `color-mix(in oklch, ${meta.color} 6%, var(--color-bg))`,
              }}
            >
              <div
                className="flex size-10 items-center justify-center rounded-full transition-transform group-hover:scale-110"
                style={{
                  backgroundColor: `color-mix(in oklch, ${meta.color} 15%, var(--color-bg))`,
                  color: meta.color,
                }}
              >
                <Icon name="arrow" size={16} color={meta.color} />
              </div>
              <span className="text-[12px] font-semibold text-ink-2 mt-1">
                {t('common.seeAll', 'See All')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityAirbnbCard({
  activity,
  onClick,
  babyId,
}: {
  activity: ActivitySummary;
  onClick: () => void;
  babyId?: string | null;
}) {
  const t = useT();
  const enc = useEncryption();
  const setState = useSetActivityState();
  const isFavorite = !!activity.is_favorite;

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!babyId) return;
    setState.mutate({
      babyId,
      activityId: activity.id,
      is_favorite: !isFavorite,
    });
  };

  const primary = useMemo(() => {
    if (!activity.media) return null;
    const nonAudioMedia = activity.media.filter(
      (m) => m.media_type !== 'audio' && m.variant !== 'song_full' && m.variant !== 'song_instrumental' && m.variant !== 'audio'
    );
    return nonAudioMedia[0] || null;
  }, [activity.media]);

  const { state, url } = useCatalogOrBlob(
    (primary ?? { id: '', mime_type: '', is_e2e_encrypted: false }) as unknown as MediaRow,
    true, // activity.media is editorial catalog → public cacheable URL
    enc.status === 'ready',
    enc.decrypt,
    !!primary,
    true,
  );

  const categoryMeta = ACTIVITY_CATEGORY_META[activity.category] || { label: activity.category, color: '#D98E5A' };
  const categoryLabel = t('activities.category.' + activity.category, categoryMeta.label);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="w-[45%] shrink-0 snap-start flex flex-col text-left group cursor-pointer outline-none"
    >
      <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-line bg-surface mb-2.5">
        {primary && state === 'ready' && url ? (
          <img src={url} alt={activity.title} className="size-full object-cover transition-transform group-active:scale-95 duration-200" />
        ) : primary && state === 'loading' ? (
          <span className="size-full flex items-center justify-center">
            <span className="size-5 animate-pulse rounded-full bg-grey-soft" />
          </span>
        ) : (
          <div
            className="size-full flex items-center justify-center text-3xl select-none"
            style={{ background: `${categoryMeta.color}14` }}
          >
            {activity.category === 'play' ? '🧸' : activity.category === 'sing' ? '🎤' : activity.category === 'music' ? '🎵' : activity.category === 'read' ? '📚' : '🗺️'}
          </div>
        )}

        {/* Heart Favorite button overlay */}
        {babyId && (
          <button
            onClick={toggleFavorite}
            className={[
              'absolute top-2 right-2 z-15 size-8 flex items-center justify-center rounded-full backdrop-blur-md transition-all shadow-xs outline-none',
              isFavorite
                ? 'bg-accent-soft text-accent border border-accent/20'
                : 'bg-white/80 text-ink-3 hover:text-ink-2 border border-line/10',
            ].join(' ')}
          >
            <HeartIcon filled={isFavorite} size={13} />
          </button>
        )}
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: categoryMeta.color }}>
        {categoryLabel}
      </span>
      <h4 className="font-serif text-[14.5px] leading-tight tracking-tight text-ink font-semibold line-clamp-2 mt-0.5 group-hover:underline">
        {activity.title}
      </h4>
    </div>
  );
}

function HeartIcon({ filled, size = 18, className }: { filled: boolean; size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}
